/**
 * Aspect — Geometry seam seals
 * --------------------------------------------------------------
 * Thin shadow-casting strips at wall↔ceiling and wall↔floor joints, plus
 * vertical posts at wall↔wall corners. Micro-gaps (z-fight avoidance, bake
 * shell UV edges) let the HDR sky / sun bleed through as glowing white lines.
 * These seals plug the gaps without darkening the room.
 *
 * The seals sit INSIDE the wall mass and use the near-black skirting albedo, so
 * the only thing the camera can ever see of them is whatever shows through the
 * hairline they are plugging — which then reads as a contact shadow instead of
 * a light leak. An earlier version pushed them 3 cm proud of the plaster in
 * ceiling white; because they carry no UV2 they missed the baked GI and lit
 * from sky ambient alone, turning every wall top into a glowing band.
 *
 * Kept as their own entities (not `_aspectKind` wall/floor/ceiling) so the baked
 * lighting path can hide procedural plaster while seals stay and keep casting.
 */

import * as pc from "playcanvas";
import { wallLength, wallTangentAt, paramOnWall } from "./wall_geometry.js";

const AXIS_EPS = 1e-3;
const MIN_LEN = 0.05;
/** How far seals poke into the ceiling / below the floor (meters). */
export const SEAM_OVERLAP = 0.07;
/**
 * Held just clear of both wall faces: enough to never z-fight the plaster, small
 * enough that the strip still backs the hairline right up to the room-side edge.
 */
const SEAM_INSET = 0.002;
/** Shared-endpoint tolerance for detecting an L-corner between two walls. */
const CORNER_JOIN_EPS = 0.03;
/** Floor seals stop this far clear of door cutouts (jamb trim covers the rest). */
const DOOR_GAP_PAD = 0.03;
/** Corner posts grow this much past the exterior wall faces (never room-side). */
const CORNER_PAD = 0.02;

function boxEntity(name, size, material) {
  const e = new pc.Entity(name);
  e.addComponent("render", {
    type: "box",
    material,
    castShadows: true,
    receiveShadows: true,
  });
  e.setLocalScale(size[0], size[1], size[2]);
  e._aspectSeamSeal = true;
  return e;
}

/** Drop previously installed seals (room rebuild / bake reactivate). */
export function clearSeamSeals(roomRoot) {
  if (!roomRoot) return;
  for (const child of [...roomRoot.children]) {
    if (child._aspectSeamSeal || String(child.name ?? "").startsWith("seam-seal-")) {
      child.destroy();
    }
  }
}

/**
 * Install wall-top and wall-base seals for every solid (non-view) wall.
 * @param {pc.Entity} roomRoot
 * @param {object} room
 * @param {{ wall: pc.StandardMaterial, baseboard?: pc.StandardMaterial }} surf
 */
export function installSeamSeals(roomRoot, room, surf) {
  clearSeamSeals(roomRoot);
  if (!roomRoot || !room?.walls?.length || !surf?.wall) return;

  const floorY = room.floorY ?? 0;
  const ceilH = room.dimensions?.height ?? 2.8;
  const mat = surf.wall;

  for (const w of room.walls) {
    if (w.view) continue;
    const len = wallLength(w);
    if (len < MIN_LEN) continue;

    const ax = w.start[0];
    const az = w.start[1];
    const bx = w.end[0];
    const bz = w.end[1];
    const dx = bx - ax;
    const dz = bz - az;
    const ux = dx / len;
    const uz = dz / len;
    const thickness = w.thickness ?? 0.15;
    const nx = Number(w.normal?.[0]) || 0;
    const nz = Number(w.normal?.[1]) || 0;
    // Center the seal on the wall mass (same offset as plaster).
    const ox = nx ? -nx * (thickness / 2) : 0;
    const oz = nz ? -nz * (thickness / 2) : 0;
    const cx = (ax + bx) / 2 + ox;
    const cz = (az + bz) / 2 + oz;
    const alongZ = Math.abs(dx) < AXIS_EPS;
    const yaw = !alongZ && Math.abs(dz) >= AXIS_EPS ? (Math.atan2(-dz, dx) * 180) / Math.PI : 0;
    const thick = Math.max(0.02, thickness - SEAM_INSET * 2);
    // Near-black skirting albedo: what shows through a hairline reads as shadow.
    const sealMat = surf.baseboard ?? mat;

    const topH = SEAM_OVERLAP * 2;
    const topSize = alongZ ? [thick, topH, len + 0.02] : [len + 0.02, topH, thick];
    const top = boxEntity(`seam-seal-top-${w.id}`, topSize, sealMat);
    top.setLocalPosition(cx, floorY + ceilH, cz);
    if (yaw) top.setLocalEulerAngles(0, yaw, 0);
    roomRoot.addChild(top);

    // Floor seal — segmented around door cutouts. A full-length strip crosses
    // every doorway as a visible threshold bar (black in daylight, a white
    // specular streak under night lamps); there is no wall there to seal.
    const doorGaps = (room.openings ?? [])
      .filter((o) => o.wall === w.id && o.type === "door")
      .map((o) => {
        const dist = paramOnWall(w, o.center[0], o.center[2]) * len;
        const half = (o.width ?? 0.9) / 2 + DOOR_GAP_PAD;
        return [Math.max(0, dist - half), Math.min(len, dist + half)];
      })
      .sort((a, b) => a[0] - b[0]);

    const spans = [];
    let sCursor = 0;
    for (const [g0, g1] of doorGaps) {
      if (g0 - sCursor >= MIN_LEN) spans.push([sCursor, g0]);
      sCursor = Math.max(sCursor, g1);
    }
    if (len - sCursor >= MIN_LEN) spans.push([sCursor, len]);

    const botH = SEAM_OVERLAP * 1.8;
    for (const [s0, s1] of spans) {
      // Keep the 1cm overshoot at true wall ends so corners stay sealed.
      const e0 = s0 === 0 ? -0.01 : s0;
      const e1 = s1 === len ? len + 0.01 : s1;
      const segLen = e1 - e0;
      const mid = (e0 + e1) / 2;
      const botSize = alongZ ? [thick, botH, segLen] : [segLen, botH, thick];
      const bot = boxEntity(`seam-seal-bot-${w.id}-${s0.toFixed(2)}`, botSize, sealMat);
      bot.setLocalPosition(ax + ux * mid + ox, floorY, az + uz * mid + oz);
      if (yaw) bot.setLocalEulerAngles(0, yaw, 0);
      roomRoot.addChild(bot);
    }
  }

  installCornerPosts(roomRoot, room, surf);
}

/** Ray-cast point-in-polygon on the floor plane (poly = [[x, z], ...]). */
function pointInFloorPolygon(x, z, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0];
    const zi = poly[i][1];
    const xj = poly[j][0];
    const zj = poly[j][1];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Vertical posts filling the hollow column at every PERIMETER wall↔wall
 * L-corner.
 *
 * Plaster boxes sit on the exterior side of their wall line, so at a corner
 * two walls only touch along a zero-width vertical edge — the thickness×
 * thickness column between their exterior faces is empty. Shadow-map bias
 * leaks sun/sky through that near-zero-depth occluder and paints a glowing
 * vertical hairline down the interior corner. The post fills the column
 * flush with both wall end faces (full face-to-face butts, no room-side
 * protrusion), giving the corner real occluder depth.
 *
 * Interior partition junctions are skipped: there the corner column sits
 * INSIDE a neighboring room, so a post would stand in living space and
 * z-fight the walls (and there is no sky above it to leak anyway).
 */
function installCornerPosts(roomRoot, room, surf) {
  const floorY = room.floorY ?? 0;
  const ceilH = room.dimensions?.height ?? 2.8;
  const sealMat = surf.baseboard ?? surf.wall;
  const poly = Array.isArray(room.floorPolygon) && room.floorPolygon.length >= 3 ? room.floorPolygon : null;
  const b = room.bounds;
  const insideFootprint = (x, z) => {
    if (poly) return pointInFloorPolygon(x, z, poly);
    if (b?.min && b?.max) return x > b.min[0] && x < b.max[0] && z > b.min[2] && z < b.max[2];
    return false;
  };

  const walls = room.walls.filter((w) => !w.view && wallLength(w) >= MIN_LEN);
  let postIdx = 0;

  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const A = walls[i];
      const B = walls[j];
      for (const endA of [0, 1]) {
        const pa = endA ? A.end : A.start;
        for (const endB of [0, 1]) {
          const pb = endB ? B.end : B.start;
          if (Math.hypot(pa[0] - pb[0], pa[1] - pb[1]) > CORNER_JOIN_EPS) continue;

          const nAx = Number(A.normal?.[0]) || 0;
          const nAz = Number(A.normal?.[1]) || 0;
          const nBx = Number(B.normal?.[0]) || 0;
          const nBz = Number(B.normal?.[1]) || 0;
          // Without room normals the column position is unknowable — skip
          // rather than risk poking a post into the room.
          if ((!nAx && !nAz) || (!nBx && !nBz)) continue;
          // Near-collinear joints already butt full-face; no hollow column.
          if (Math.abs(nAx * nBz - nAz * nBx) < 0.35) continue;

          const tA = A.thickness ?? 0.15;
          const tB = B.thickness ?? 0.15;
          const acrossA = tA + CORNER_PAD;
          const acrossB = tB + CORNER_PAD;
          // Interior faces stay flush with the corner point (room-side faces of
          // both walls); only the exterior sides take the pad.
          const cx = pa[0] - (nAx * acrossA + nBx * acrossB) / 2;
          const cz = pa[1] - (nAz * acrossA + nBz * acrossB) / 2;
          // Interior junction — the column is inside a neighboring room.
          if (insideFootprint(cx, cz)) continue;

          // Orient the box along wall A's tangent at the joint.
          const [tx, tz] = wallTangentAt(A, endA);
          const alongZ = Math.abs(tx) < AXIS_EPS;
          const yaw = !alongZ && Math.abs(tz) >= AXIS_EPS ? (Math.atan2(-tz, tx) * 180) / Math.PI : 0;
          // Extent along A's tangent covers B's thickness; across covers A's.
          const size = alongZ
            ? [acrossA, ceilH + SEAM_OVERLAP * 2, acrossB]
            : [acrossB, ceilH + SEAM_OVERLAP * 2, acrossA];

          const post = boxEntity(`seam-seal-corner-${postIdx++}`, size, sealMat);
          post.setLocalPosition(cx, floorY + ceilH / 2, cz);
          if (yaw) post.setLocalEulerAngles(0, yaw, 0);
          roomRoot.addChild(post);
        }
      }
    }
  }
}
